import React, { useEffect, useState, useCallback } from 'react';
import { useI18n } from '@/contexts/I18nContext';
import { TopNavigation } from '@/components/layout/TopNavigation';
import { BottomNavigation } from '@/components/layout/BottomNavigation';
import GreetingSection from '@/features/home/components/GreetingSection';
import MonthlyProgressSection from '@/features/home/components/MonthlyProgressSection';
import NextTrainingsSection from '@/features/home/components/NextTrainingsSection';
import PractitionerJourneyHighlightsSection from '@/features/home/components/PractitionerJourneyHighlightsSection';
import ConnectCommunitySection from '@/features/home/components/ConnectCommunitySection';
// SocialMatSection deprecated — feed vertical lives at /sossial
import NextClassesSection from '@/features/home/components/NextClassesSection';
import InstructorJourneyHighlightsSection from '@/features/home/components/InstructorJourneyHighlightsSection';
import RequestForClassesSection from '@/features/home/components/RequestForClassesSection';
import { useInstructor } from '@/features/profile/hooks/useInstructor';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';

const Home: React.FC = () => {
  const { t } = useI18n();
  const { isInstructor, isLoading: instructorLoading } = useInstructor();
  const [instructorMode, setInstructorMode] = useState(true);
  const queryClient = useQueryClient();
  const location = useLocation();

  // Sempre rolar para o topo quando a página Home for carregada
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);


  // Função para atualizar todos os dados
  const refreshAllData = useCallback(async () => {
    // Invalidate all relevant queries to force refresh
    await queryClient.invalidateQueries({ queryKey: ['instructor-highlights'] });
    await queryClient.invalidateQueries({ queryKey: ['practitioner-highlights'] });
    await queryClient.invalidateQueries({ queryKey: ['instructor-classes'] });
    await queryClient.invalidateQueries({ queryKey: ['trainings'] });
    await queryClient.invalidateQueries({ queryKey: ['training-goals'] });
    await queryClient.invalidateQueries({ queryKey: ['monthly-progress'] });
  }, [queryClient]);

  // Add event listeners for automatic data refresh
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshAllData();
      }
    };

    const handleFocus = () => {
      refreshAllData();
    };

    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) {
        refreshAllData();
      }
    };

    // Add listeners for different events
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handlePageShow);
    
    // Also update when user navigates back to Home
    if (location.pathname === '/') {
      refreshAllData();
    }

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [queryClient, location.pathname]);

  // Update data when component is mounted
  useEffect(() => {
    refreshAllData();
  }, []);

  // Set instructor mode based on user type
  useEffect(() => {
    if (!instructorLoading) {
      if (isInstructor) {
        setInstructorMode(true);
      } else {
        setInstructorMode(false);
      }
    }
  }, [isInstructor, instructorLoading]);

  const handleInstructorModeToggle = (checked: boolean) => {
    setInstructorMode(checked);
  };

  return (
    <>
      <TopNavigation variant="subtle" />
      <main className="min-h-screen text-white py-24">
        <div className="px-4" style={{ maxWidth: '100vw' }}>
          <GreetingSection />
          
          {/* Instructor Mode Toggle - Only show for instructors */}
          {isInstructor && !instructorLoading && (
            <div className="mb-6 flex items-center space-x-3">
              <Switch
                id="instructor-mode"
                checked={instructorMode}
                onCheckedChange={handleInstructorModeToggle}
                className="h-6 w-11 bg-[#374151] data-[state=checked]:bg-sossiety-red data-[state=unchecked]:bg-[#374151] border border-[#4B5563] [&>span]:bg-[#F3F4F6] [&>span]:rounded-[10px] transition-colors duration-200"
              />
              <Label htmlFor="instructor-mode" className="text-white text-sm font-medium">
                {t('home.instructor_mode.title')}
              </Label>
            </div>
          )}

          <MonthlyProgressSection showForPractitionerMode={!instructorMode} />
          
          {/* Conditional rendering based on mode */}
          {instructorMode && isInstructor ? (
            // Instructor Mode Sections
            <>
              <RequestForClassesSection />
              <NextClassesSection />
              <InstructorJourneyHighlightsSection />
            </>
          ) : (
            // Practitioner Mode Sections
            <>
              <NextTrainingsSection />
              <PractitionerJourneyHighlightsSection />
            </>
          )}
          
          <ConnectCommunitySection />
          {/* SocialMatSection removed — see /sossial for the feed */}
        </div>
      </main>
      <BottomNavigation />
      
    </>
  );
};

export default Home;
